import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import Swal from "sweetalert2";
import { FiUser, FiMail, FiShield, FiX, FiEdit3, FiSave } from 'react-icons/fi';
import apiClient from '../../../api/apiClient';

function EditUserModal({ isOpen, onClose, user, onEdit }) {
  const [first_name, setFirst_name] = useState("");
  const [last_name, setLast_name] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      setFirst_name(user.first_name || "");
      setLast_name(user.last_name || "");
      setEmail(user.email || "");
      setRole(user.role || "admin"); 
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const updatedUser = { ...user, first_name, last_name, email, role };
      
      await onEdit(updatedUser);

      Swal.fire({
        icon: 'success',
        title: 'User Updated Successfully!',
        text: `${first_name} ${last_name}'s information has been updated.`,
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        background: 'linear-gradient(135deg, #10b981, #059669)',
        color: 'white'
      });

      onClose();
    } catch (error) {
      const errorResponse = error.response?.data;
      let errorMessage = 'An error occurred during update.';

      if (errorResponse) {
        const errors = Object.values(errorResponse).flat();
        errorMessage = errors.join(' ');
      }

      Swal.fire({
        icon: 'error',
        title: 'Update Failed',
        text: errorMessage,
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        background: 'linear-gradient(135deg, #ef4444, #dc2626)',
        color: 'white'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getRoleColor = (roleValue) => {
    const colors = {
      admin: "from-green-500 to-teal-500",
      reviewer: "from-blue-500 to-cyan-500",
      tasker: "from-orange-500 to-amber-500"
    };
    return colors[roleValue] || "from-gray-500 to-gray-600";
  };

  const getUserInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-50">
      <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 dark:border-gray-700/30 max-w-2xl w-full mx-4 overflow-hidden">
        {/* Header with User Avatar */}
        <div className="relative bg-gradient-to-r from-blue-500 to-purple-600 p-8 text-white">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center text-xl font-bold">
                {getUserInitials(first_name, last_name)}
              </div>
              <div>
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  <FiEdit3 className="w-6 h-6" />
                  Edit User
                </h2>
                <p className="text-blue-100 mt-1">Update {first_name || 'user'}'s information</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center transition-colors"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          {/* Current Role Display */}
          <div className="bg-gradient-to-r from-gray-50/50 to-gray-100/50 dark:from-gray-800/50 dark:to-gray-700/50 backdrop-blur-sm rounded-xl p-4 border border-gray-200/50 dark:border-gray-600/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">Current Role</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 capitalize">{role}</p>
                </div>
              </div>
              <div className={`px-3 py-1 rounded-full bg-gradient-to-r ${getRoleColor(role)} text-white text-sm font-medium`}>
                {role?.charAt(0).toUpperCase() + role?.slice(1)}
              </div>
            </div>
          </div>

          {/* Name Fields Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="group">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                <FiUser className="inline w-4 h-4 mr-2 text-blue-500" />
                First Name
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={first_name}
                  onChange={(e) => setFirst_name(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50/50 dark:bg-gray-800/50 backdrop-blur-sm text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all group-hover:border-blue-300"
                  placeholder="Enter first name"
                  required
                />
              </div>
            </div>
            
            <div className="group">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                <FiUser className="inline w-4 h-4 mr-2 text-blue-500" />
                Last Name
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={last_name}
                  onChange={(e) => setLast_name(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50/50 dark:bg-gray-800/50 backdrop-blur-sm text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all group-hover:border-blue-300"
                  placeholder="Enter last name"
                  required
                />
              </div>
            </div>
          </div>

          {/* Email Field */}
          <div className="group">
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              <FiMail className="inline w-4 h-4 mr-2 text-blue-500" />
              Email Address
            </label>
            <div className="relative">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50/50 dark:bg-gray-800/50 backdrop-blur-sm text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all group-hover:border-blue-300"
                placeholder="Enter email address"
                required
              />
            </div>
          </div>

          {/* Role Selection with Visual Cards */}
          <div className="group">
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
              <FiShield className="inline w-4 h-4 mr-2 text-blue-500" />
              Update Role
            </label>
            <div className="grid grid-cols-3 gap-3">
              {["admin", "reviewer", "tasker"].map((roleOption) => (
                <label key={roleOption} className="cursor-pointer">
                  <input
                    type="radio"
                    name="role"
                    value={roleOption}
                    checked={role === roleOption}
                    onChange={(e) => setRole(e.target.value)}
                    className="sr-only"
                  />
                  <div className={`p-4 rounded-xl border-2 transition-all text-center ${
                    role === roleOption
                      ? `bg-gradient-to-r ${getRoleColor(roleOption)} text-white border-transparent shadow-lg scale-105`
                      : "border-gray-200 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-800/50 hover:border-blue-300 hover:bg-blue-50/50 dark:hover:bg-blue-900/20"
                  }`}>
                    <div className={`font-semibold capitalize text-sm ${
                      role === roleOption ? "text-white" : "text-gray-700 dark:text-gray-300"
                    }`}>
                      {roleOption}
                    </div>
                    <div className={`text-xs mt-1 ${
                      role === roleOption ? "text-blue-100" : "text-gray-500 dark:text-gray-400"
                    }`}>
                      {roleOption === "admin" ? "Full access" : 
                       roleOption === "reviewer" ? "Review & approve" : "Task execution"}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Warning Message for Role Change */}
          {user && role !== user.role && (
            <div className="bg-amber-50/50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-amber-100 dark:bg-amber-900/50 rounded-full flex items-center justify-center">
                  <span className="text-amber-600 dark:text-amber-400">⚠️</span>
                </div>
                <div>
                  <p className="font-medium text-amber-800 dark:text-amber-300">Role Change Detected</p>
                  <p className="text-sm text-amber-700 dark:text-amber-400">
                    Changing from <span className="font-semibold capitalize">{user.role}</span> to <span className="font-semibold capitalize">{role}</span> will update user permissions immediately.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-xl transition-all font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded-xl transition-all font-medium shadow-lg hover:shadow-xl ${
                isSubmitting ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Updating...
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <FiSave className="w-4 h-4" />
                  Save Changes
                </div>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

export default EditUserModal;